import { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { sharepointService } from '../../services/sharepointService';
import toast from 'react-hot-toast';

interface SharePointSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectionId: string;
  onComplete: () => void;
}

type SetupStep = 'select' | 'create' | 'existing' | 'progress' | 'complete';

interface SharePointSite {
  id: string;
  name: string;
  displayName: string;
  webUrl: string;
}

export function SharePointSetupModal({ isOpen, onClose, connectionId, onComplete }: SharePointSetupModalProps) {
  const [step, setStep] = useState<SetupStep>('select');
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState<SharePointSite[]>([]);
  const [newSiteName, setNewSiteName] = useState('PrivionCLM');
  const [newSiteDescription, setNewSiteDescription] = useState('Contract Lifecycle Management Documents');
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [progress, setProgress] = useState<string[]>([]);

  // Load existing sites when modal opens
  useEffect(() => {
    if (isOpen && step === 'select') {
      loadSites();
    }
  }, [isOpen, step]);

  const loadSites = async () => {
    setLoading(true);
    try {
      const userSites = await sharepointService.listSites(connectionId);
      setSites(userSites);
    } catch (error) {
      console.error('Failed to load sites:', error);
      toast.error('Failed to load SharePoint sites');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSite = async () => {
    if (!newSiteName.trim()) {
      toast.error('Please enter a site name');
      return;
    }

    setStep('progress');
    setProgress(['Creating SharePoint site...']);

    try {
      // Create the site
      const site = await sharepointService.createSite(connectionId, {
        name: newSiteName,
        description: newSiteDescription,
      });
      
      setProgress(prev => [...prev, `✓ Site created: ${site.displayName}`]);
      setProgress(prev => [...prev, 'Setting up folder structure...']);

      // Setup folders
      await sharepointService.setupFolders(connectionId, site.id);
      
      setProgress(prev => [...prev, '✓ Templates folder created']);
      setProgress(prev => [...prev, '✓ Generated Contracts folder created']);
      setProgress(prev => [...prev, '✓ Signed Contracts folder created']);
      setProgress(prev => [...prev, '✓ Setup complete!']);

      setStep('complete');
      toast.success('SharePoint site created and configured!');
      
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (error) {
      console.error('Failed to create site:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create site';
      setProgress(prev => [...prev, `✗ Error: ${errorMessage}`]);
      toast.error(errorMessage);
      
      // Allow user to go back
      setTimeout(() => {
        setStep('select');
        setProgress([]);
      }, 3000);
    }
  };

  const handleUseExistingSite = async () => {
    if (!selectedSiteId) {
      toast.error('Please select a site');
      return;
    }

    const selectedSite = sites.find(s => s.id === selectedSiteId);
    if (!selectedSite) return;

    setStep('progress');
    setProgress([`Connecting to: ${selectedSite.displayName}...`]);

    try {
      // Setup folders in existing site
      setProgress(prev => [...prev, 'Setting up folder structure...']);
      
      await sharepointService.setupFolders(connectionId, selectedSiteId);
      
      setProgress(prev => [...prev, '✓ Templates folder ready']);
      setProgress(prev => [...prev, '✓ Generated Contracts folder ready']);
      setProgress(prev => [...prev, '✓ Signed Contracts folder ready']);
      setProgress(prev => [...prev, '✓ Setup complete!']);

      setStep('complete');
      toast.success('SharePoint configured successfully!');
      
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (error) {
      console.error('Failed to setup folders:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to setup folders';
      setProgress(prev => [...prev, `✗ Error: ${errorMessage}`]);
      toast.error(errorMessage);
      
      setTimeout(() => {
        setStep('select');
        setProgress([]);
      }, 3000);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => step === 'progress' ? undefined : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between"
                >
                  Setup SharePoint Integration
                  {step !== 'progress' && (
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  )}
                </Dialog.Title>

                <div className="mt-4">
                  {/* Step: Select Mode */}
                  {step === 'select' && (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-500">
                        Choose how you want to set up SharePoint for contract management:
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Create New Site */}
                        <button
                          onClick={() => setStep('create')}
                          className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left group"
                        >
                          <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4 group-hover:bg-blue-200">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-2">Create New Site</h4>
                          <p className="text-sm text-gray-600">
                            Create a dedicated SharePoint site for Privion CLM
                          </p>
                        </button>

                        {/* Use Existing Site */}
                        <button
                          onClick={() => setStep('existing')}
                          className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left group"
                        >
                          <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-4 group-hover:bg-green-200">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-2">Use Existing Site</h4>
                          <p className="text-sm text-gray-600">
                            Connect to an existing SharePoint site
                          </p>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step: Create New Site Form */}
                  {step === 'create' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Site Name *
                        </label>
                        <input
                          type="text"
                          value={newSiteName}
                          onChange={(e) => setNewSiteName(e.target.value)}
                          placeholder="PrivionCLM"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          This will be used in the SharePoint URL
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          value={newSiteDescription}
                          onChange={(e) => setNewSiteDescription(e.target.value)}
                          rows={3}
                          placeholder="Contract Lifecycle Management Documents"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h5 className="font-medium text-blue-900 mb-2">What will be created:</h5>
                        <ul className="text-sm text-blue-800 space-y-1">
                          <li>• SharePoint communication site</li>
                          <li>• Templates folder (for contract templates)</li>
                          <li>• Generated Contracts folder (for generated documents)</li>
                          <li>• Signed Contracts folder (for finalized contracts)</li>
                        </ul>
                      </div>

                      <div className="flex gap-3 justify-end pt-4">
                        <button
                          onClick={() => setStep('select')}
                          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                          Back
                        </button>
                        <button
                          onClick={handleCreateSite}
                          disabled={!newSiteName.trim() || loading}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? 'Creating...' : 'Create Site'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step: Select Existing Site */}
                  {step === 'existing' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select a SharePoint Site *
                        </label>
                        {loading ? (
                          <div className="text-center py-8 text-gray-500">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                            Loading sites...
                          </div>
                        ) : sites.length > 0 ? (
                          <select
                            value={selectedSiteId}
                            onChange={(e) => setSelectedSiteId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Choose a site...</option>
                            {sites.map((site) => (
                              <option key={site.id} value={site.id}>
                                {site.displayName}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            No sites found. Try creating a new one instead.
                          </div>
                        )}
                      </div>

                      {selectedSiteId && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h5 className="font-medium text-green-900 mb-2">Folders will be created:</h5>
                          <ul className="text-sm text-green-800 space-y-1">
                            <li>• Templates</li>
                            <li>• Generated Contracts</li>
                            <li>• Signed Contracts</li>
                          </ul>
                          <p className="text-xs text-green-700 mt-2">
                            Existing folders with these names will be used if they already exist.
                          </p>
                        </div>
                      )}

                      <div className="flex gap-3 justify-end pt-4">
                        <button
                          onClick={() => setStep('select')}
                          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                          Back
                        </button>
                        <button
                          onClick={handleUseExistingSite}
                          disabled={!selectedSiteId || loading}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? 'Setting up...' : 'Use This Site'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step: Progress */}
                  {step === 'progress' && (
                    <div className="space-y-4">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        {progress.map((message, index) => (
                          <div key={index} className="flex items-start gap-2 py-1 text-sm">
                            {message.startsWith('✓') ? (
                              <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            ) : message.startsWith('✗') ? (
                              <XMarkIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            ) : (
                              <div className="w-5 h-5 flex-shrink-0 mt-0.5">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              </div>
                            )}
                            <span className={message.startsWith('✗') ? 'text-red-700' : 'text-gray-700'}>
                              {message.replace(/^[✓✗]\s*/, '')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step: Complete */}
                  {step === 'complete' && (
                    <div className="text-center py-8">
                      <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                          <CheckCircleIcon className="w-10 h-10 text-green-600" />
                        </div>
                      </div>
                      <h4 className="text-xl font-semibold text-gray-900 mb-2">
                        SharePoint Setup Complete!
                      </h4>
                      <p className="text-gray-600">
                        Your SharePoint site is ready for contract management.
                      </p>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

